import { useMemo, useRef, useState } from "react";
import { filterImages, formatBytes, splitByVariant } from "../lib/tasks";
import type { TeamForm } from "../lib/types";

/** PDF §1: her kategoride 1.000 görüntü bekleniyor. */
const EXPECTED_COUNT = 1000;

interface Props {
  form: TeamForm;
  onBack: () => void;
  onStart: (files: File[]) => void;
}

async function filesFromDataTransfer(dt: DataTransfer): Promise<File[]> {
  const entries: FileSystemEntry[] = [];
  for (const item of Array.from(dt.items)) {
    const entry = item.webkitGetAsEntry?.();
    if (entry) entries.push(entry);
  }
  if (entries.length === 0) return Array.from(dt.files);

  const out: File[] = [];
  async function walk(entry: FileSystemEntry): Promise<void> {
    if (entry.isFile) {
      const file = await new Promise<File>((resolve, reject) =>
        (entry as FileSystemFileEntry).file(resolve, reject)
      );
      out.push(file);
      return;
    }
    const reader = (entry as FileSystemDirectoryEntry).createReader();
    for (;;) {
      // readEntries returns at most 100 per call, so it must be drained in a loop.
      const batch = await new Promise<FileSystemEntry[]>((resolve, reject) =>
        reader.readEntries(resolve, reject)
      );
      if (batch.length === 0) break;
      for (const e of batch) await walk(e);
    }
  }
  for (const e of entries) await walk(e);
  return out;
}

export function FolderPicker({ form, onBack, onStart }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [rejected, setRejected] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [scanning, setScanning] = useState(false);

  const kindLabel = form.dataKind === "thermal" ? "termal" : "RGB";
  const totalBytes = files.reduce((s, f) => s + f.size, 0);

  const { matching, mismatched } = useMemo(
    () => splitByVariant(files, form.dataKind),
    [files, form.dataKind]
  );

  function accept(picked: File[]) {
    const { accepted, rejected: bad } = filterImages(picked);
    setFiles(accepted);
    setRejected(bad.length);
  }

  async function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragging(false);
    setScanning(true);
    try {
      accept(await filesFromDataTransfer(e.dataTransfer));
    } finally {
      setScanning(false);
    }
  }

  return (
    <div className="card">
      <h2>2. {kindLabel} klasörünü seçiniz</h2>
      <p className="muted">
        {form.provinceName} · {form.teamName} · <strong>{kindLabel}</strong> seti
      </p>

      <div
        className={`dropzone${dragging ? " over" : ""}`}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") inputRef.current?.click();
        }}
      >
        {scanning ? (
          <p>Klasör taranıyor…</p>
        ) : (
          <>
            <p className="dz-title">Klasörü buraya sürükleyin</p>
            <p className="muted">veya tıklayarak seçin</p>
          </>
        )}
        <input
          ref={inputRef}
          type="file"
          multiple
          // @ts-expect-error - non-standard but supported in Chrome, Edge, Safari, Firefox
          webkitdirectory=""
          directory=""
          hidden
          onChange={(e) => accept(Array.from(e.target.files ?? []))}
        />
      </div>

      {files.length > 0 && (
        <div className="summary">
          <div className="stat">
            <span className="big">{files.length.toLocaleString("tr-TR")}</span>
            <span className="muted">görüntü</span>
          </div>
          <div className="stat">
            <span className="big">{formatBytes(totalBytes)}</span>
            <span className="muted">toplam boyut</span>
          </div>

          {rejected > 0 && (
            <p className="warn">
              {rejected} dosya görüntü olmadığı için listeye alınmadı (yalnızca JPG, PNG, TIFF, DNG
              kabul edilir).
            </p>
          )}

          {mismatched.length > 0 && (
            <div className="mismatch">
              <p className="warn">
                {form.dataKind === "thermal" ? (
                  <>
                    Veri tipi olarak <strong>Termal</strong> seçtiniz, ancak{" "}
                    {mismatched.length.toLocaleString("tr-TR")} görüntüde DJI termal eki{" "}
                    <code>_T</code> yok. Bu dosyalar büyük olasılıkla RGB görüntülerdir.
                  </>
                ) : (
                  <>
                    Veri tipi olarak <strong>RGB</strong> seçtiniz, ancak{" "}
                    {mismatched.length.toLocaleString("tr-TR")} görüntü <code>_T</code> ekiyle
                    termal görünüyor.
                  </>
                )}
              </p>
              {matching.length > 0 ? (
                <button type="button" onClick={() => setFiles(matching)}>
                  Sadece {kindLabel} görüntüleri bırak ({matching.length.toLocaleString("tr-TR")})
                </button>
              ) : (
                <p className="warn">
                  Seçilen klasörde {kindLabel} görüntü bulunamadı. Yanlış klasörü ya da yanlış veri
                  tipini seçmiş olabilirsiniz.
                </p>
              )}
            </div>
          )}
          {files.length < EXPECTED_COUNT && (
            <p className="warn">
              Şartnamede kategori başına {EXPECTED_COUNT.toLocaleString("tr-TR")} görüntü
              isteniyor. Seçiminiz {files.length.toLocaleString("tr-TR")} görüntü içeriyor —
              yüklemeye yine de devam edebilirsiniz.
            </p>
          )}
        </div>
      )}

      <div className="actions">
        <button type="button" onClick={onBack}>
          ← Geri
        </button>
        <button
          type="button"
          className="primary"
          disabled={files.length === 0}
          onClick={() => onStart(files)}
        >
          Yüklemeyi başlat
        </button>
      </div>
    </div>
  );
}
